import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Ellipsis, ExternalLink, Mail, Pencil, Plus, Trash2, User } from 'lucide-react';
import { customersApi, partnersApi, interactionsApi } from '@/lib/api';
import {
  ActivityItem,
  Button,
  ChipGroup,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Faelligkeit,
  IconButton,
  Spinner,
  Status,
  formatDatumZeit,
  interaktionInfo,
  type ActivityType as VerlaufTyp,
} from '@elbklar/ui';
import { InteractionSheet } from './InteractionSheet';
import type { ActivityFeedItem, ActivityType, InteractionType } from '@/types';

const typeFilterOptions: { value: ActivityType | ''; label: string }[] = [
  { value: '', label: 'Alle' },
  { value: 'interaction', label: 'Interaktionen' },
  { value: 'email', label: 'E-Mails' },
  { value: 'task', label: 'Aufgaben' },
];

/** Verlaufstyp des Design Systems (Icon und Ton fest) je Eintrag. */
function verlauf(item: ActivityFeedItem): VerlaufTyp {
  if (item.activity_type === 'email') return 'mail';
  if (item.activity_type === 'task') return 'task';
  return interaktionInfo(item.interaction_type || '').type;
}

interface ActivityTimelineProps {
  entityType: 'customer' | 'partner';
  entityId: string;
  onAddInteraction: () => void;
}

export function ActivityTimeline({ entityType, entityId, onAddInteraction }: ActivityTimelineProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<ActivityType | ''>('');
  const [editingInteraction, setEditingInteraction] = useState<ActivityFeedItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchFeed = entityType === 'customer'
    ? () => customersApi.getActivityFeed(entityId, { limit: 50, type: typeFilter || undefined })
    : () => partnersApi.getActivityFeed(entityId, { limit: 50, type: typeFilter || undefined });

  const { data, isLoading } = useQuery({
    queryKey: ['activity-feed', entityType, entityId, typeFilter],
    queryFn: fetchFeed,
    enabled: !!entityId,
  });

  // Zum Bearbeiten die ganze Interaktion laden: Der Feed kennt die
  // Wiedervorlage nicht, das Formular soll sie vorbelegen. Schlägt das Laden
  // fehl, öffnet es mit den Daten aus dem Feed; eine Wiedervorlage schickt
  // das Formular dann nur, wenn sie geändert wird.
  const { data: zuBearbeiten, isLoading: laedtZumBearbeiten } = useQuery({
    queryKey: ['interactions', editingInteraction?.id],
    queryFn: () => interactionsApi.get(editingInteraction!.id),
    enabled: !!editingInteraction,
  });
  const vollstaendig = zuBearbeiten && zuBearbeiten.id === editingInteraction?.id ? zuBearbeiten : undefined;
  const bearbeiten = vollstaendig
    ? {
        id: vollstaendig.id,
        type: vollstaendig.type,
        subject: vollstaendig.subject,
        description: vollstaendig.description ?? '',
        occurred_at: vollstaendig.occurred_at,
        follow_up_date: vollstaendig.follow_up_date,
      }
    : editingInteraction
      ? {
          id: editingInteraction.id,
          // Der Aktivitaets-Feed liefert interaction_type als freien String
          // (oder null). Der Fallback 'note' entspricht dem bisherigen
          // Verhalten: das Formular setzte bei fehlendem Typ ebenfalls
          // 'note' als Vorgabe.
          type: (editingInteraction.interaction_type || 'note') as InteractionType,
          subject: editingInteraction.title,
          description: editingInteraction.description || '',
          occurred_at: editingInteraction.timestamp,
        }
      : undefined;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => interactionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
      queryClient.invalidateQueries({ queryKey: ['interactions'] });
    },
  });

  const items = data?.items || [];

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <ChipGroup
          label="Aktivitäten filtern"
          value={typeFilter || 'alle'}
          onChange={(v) => setTypeFilter(v === 'alle' ? '' : (v as ActivityType))}
          items={typeFilterOptions.map((opt) => ({ value: opt.value || 'alle', label: opt.label }))}
        />
        <Button size="sm" icon={Plus} onClick={onAddInteraction}>
          Interaktion anlegen
        </Button>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size={24} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={Mail} title="Keine Aktivitäten" description="Interaktionen, E-Mails und Aufgaben erscheinen hier." />
      ) : (
        <div>
          {items.map((item, idx) => {
            const schluessel = `${item.activity_type}-${item.id}`;
            const isExpanded = expandedId === schluessel;
            const art =
              item.activity_type === 'interaction'
                ? interaktionInfo(item.interaction_type || '').label
                : item.activity_type === 'email'
                  ? item.email_direction === 'inbound'
                    ? 'E-Mail erhalten'
                    : 'E-Mail gesendet'
                  : 'Aufgabe';
            return (
              <ActivityItem
                key={schluessel}
                type={verlauf(item)}
                last={idx === items.length - 1}
                title={item.title || '(Kein Betreff)'}
                meta={
                  <span className="inline-flex items-center gap-1">
                    {art} ·{' '}
                    {formatDatumZeit(item.timestamp)}
                    {item.activity_type === 'interaction' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <IconButton icon={Ellipsis} label="Aktionen" variant="ghost" size="sm" noTooltip />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem icon={ExternalLink} onSelect={() => navigate(`/interactions/${item.id}`)}>
                            Öffnen
                          </DropdownMenuItem>
                          <DropdownMenuItem icon={Pencil} onSelect={() => setEditingInteraction(item)}>
                            Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuItem icon={Trash2} tone="danger" onSelect={() => setDeletingId(item.id)}>
                            Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </span>
                }
              >
                <div className="cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : schluessel)}>
                  {item.description && (
                    <p
                      className={`whitespace-pre-wrap t-small ${
                        isExpanded ? '' : item.activity_type === 'email' ? 'line-clamp-6' : 'line-clamp-3'
                      }`}
                    >
                      {item.description}
                    </p>
                  )}

                  {item.activity_type === 'email' && item.email_from && (
                    <div className="mt-1.5 t-small">Von: {item.email_from}</div>
                  )}

                  {item.activity_type === 'task' && (
                    <div className="mt-2 flex items-center gap-2">
                      {item.task_status && <Status status={`task_${item.task_status}`} />}
                      {item.task_due_date && (
                        <Faelligkeit datum={item.task_due_date} erledigt={item.task_status === 'completed' || item.task_status === 'cancelled'} className="t-small" />
                      )}
                    </div>
                  )}

                  {item.contact_name && item.contact_name.trim() && (
                    <div className="mt-1 flex items-center gap-1 t-small">
                      <User size={12} strokeWidth={1.75} aria-hidden />
                      {item.contact_name}
                    </div>
                  )}

                  {item.description && !isExpanded && item.description.length > 100 && (
                    <span className="mt-1 inline-block t-small text-link">Mehr anzeigen</span>
                  )}
                </div>
              </ActivityItem>
            );
          })}
        </div>
      )}

      <InteractionSheet
        open={!!editingInteraction && !laedtZumBearbeiten}
        onOpenChange={(offen) => !offen && setEditingInteraction(null)}
        title="Interaktion bearbeiten"
        interaction={bearbeiten}
        customerId={entityType === 'customer' ? entityId : undefined}
        partnerId={entityType === 'partner' ? entityId : undefined}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingId}
        onOpenChange={(offen) => !offen && setDeletingId(null)}
        title="Interaktion löschen?"
        description="Die Interaktion wird endgültig gelöscht."
        confirmLabel="Interaktion löschen"
        tone="danger"
        onConfirm={() => (deletingId ? deleteMutation.mutateAsync(deletingId) : undefined)}
      />
    </div>
  );
}
